import type { Location } from '../types/location.js'
import { API_BASE } from './client.js'
import { ApiError } from '../types/api.js'

function token(): string | null {
  return localStorage.getItem('mf_access')
}

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const t = token()
  if (t) headers['Authorization'] = `Bearer ${t}`
  return headers
}

async function toApiError(res: Response, fallback: string): Promise<ApiError> {
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    // non-JSON error response
  }
  const errBody =
    (body as { error?: { code: string; message: string; details?: Record<string, unknown> } } | null)
      ?.error ?? { code: 'UNKNOWN', message: fallback }
  return new ApiError(res.status, errBody)
}

export interface LocationListResponse {
  data: Location[]
  meta: { total: number; page: number; pageSize: number; totalPages: number }
}

export interface CreateLocationData {
  name: string
  slug?: string
  description?: string
  address?: string
  phone?: string
  email?: string
  timezone?: string
  latitude?: number
  longitude?: number
}

export interface UpdateLocationData {
  name?: string
  description?: string
  address?: string
  phone?: string
  email?: string
  timezone?: string
  latitude?: number
  longitude?: number
}

export async function getLocations(organizationId: string): Promise<LocationListResponse> {
  const res = await fetch(`${API_BASE}/organizations/${organizationId}/locations`, { headers: authHeaders() })
  if (!res.ok) throw await toApiError(res, 'Failed to load locations')
  return res.json() as Promise<LocationListResponse>
}

export async function getLocation(organizationId: string, locationId: string): Promise<Location> {
  const res = await fetch(`${API_BASE}/organizations/${organizationId}/locations/${locationId}`, { headers: authHeaders() })
  if (!res.ok) throw await toApiError(res, 'Failed to load location')
  const b = await res.json()
  return b.data
}

export async function createLocation(organizationId: string, data: CreateLocationData): Promise<Location> {
  const res = await fetch(`${API_BASE}/organizations/${organizationId}/locations`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
  })
  if (!res.ok) throw await toApiError(res, 'Failed to create location')
  const b = await res.json()
  return b.data
}

export async function updateLocation(organizationId: string, locationId: string, data: UpdateLocationData): Promise<Location> {
  const res = await fetch(`${API_BASE}/organizations/${organizationId}/locations/${locationId}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(data),
  })
  if (!res.ok) throw await toApiError(res, 'Failed to update location')
  const b = await res.json()
  return b.data
}

async function lifecycleLocation(
  organizationId: string,
  locationId: string,
  action: string,
): Promise<Location> {
  const res = await fetch(
    `${API_BASE}/organizations/${organizationId}/locations/${locationId}/${action}`,
    { method: 'POST', headers: authHeaders() },
  )
  if (!res.ok) throw await toApiError(res, 'Failed to change location status')
  const b = await res.json()
  return b.data
}

export function suspendLocation(organizationId: string, locationId: string): Promise<Location> {
  return lifecycleLocation(organizationId, locationId, 'suspend')
}

export function activateLocation(organizationId: string, locationId: string): Promise<Location> {
  return lifecycleLocation(organizationId, locationId, 'activate')
}

export function archiveLocation(organizationId: string, locationId: string): Promise<Location> {
  return lifecycleLocation(organizationId, locationId, 'archive')
}